import { create } from 'zustand';

import { TOOL_CONST } from '@/constant';
import { uuid } from '@/package';
import type {
  BoxWithLabel,
  ReviewStoreActions,
  ReviewStoreState,
} from '@/type';
import { eulerToQuaternion } from '@/util';

import usePcdRenderingStore from './pcdRenderingStore';

const useReviewStore = create<ReviewStoreState & ReviewStoreActions>(
  (set, get) => {
    const getCurrentPcdIndex = () => usePcdRenderingStore.getState().pcdIndex;

    return {
      comment: '',
      reviewBoxMode: false,
      pcdIndexReviewBoxListMap: {},
      reviewCommentVisible: false,

      setReviewBoxListWithIndex(boxList, index) {
        const prevPcdIndexBoxListMap = get().pcdIndexReviewBoxListMap;
        const pcdIndex = index ?? getCurrentPcdIndex();

        set({
          pcdIndexReviewBoxListMap: {
            ...prevPcdIndexBoxListMap,
            [pcdIndex]: boxList,
          },
        });
      },

      addBox(box) {
        const id = (box as BoxWithLabel).id ?? uuid();
        const boxRotation = (box as BoxWithLabel).rotation ?? [0, 0, 0];
        const boxWithLabel = {
          ...box,
          id,
          quaternion: eulerToQuaternion(boxRotation),
          rotation: boxRotation,
          position: [box.position[0], box.position[1], box.dimension[2] / 2],
          workType: TOOL_CONST.TOOL_LABELING_BOX_WORK_TYPE.REVIEW,
        };

        const prevPcdIndexBoxListMap = get().pcdIndexReviewBoxListMap;
        const pcdIndex = getCurrentPcdIndex();
        const boxList = prevPcdIndexBoxListMap[pcdIndex] ?? [];

        get().setReviewBoxListWithIndex([
          ...boxList,
          boxWithLabel as BoxWithLabel,
        ]);
      },

      removeBox(boxId) {
        const prevPcdIndexBoxListMap = get().pcdIndexReviewBoxListMap;
        const pcdIndex = getCurrentPcdIndex();
        const boxList = prevPcdIndexBoxListMap[pcdIndex];
        const newBoxList = boxList.filter(b => b.id !== boxId);

        get().setReviewBoxListWithIndex(newBoxList);
      },

      setComment(comment) {
        set({ comment });
      },

      clearComment() {
        set({ comment: '' });
      },

      setReviewBoxMode(reviewBoxMode) {
        set({
          reviewBoxMode,
        });
      },

      getCurrentPcdIndexReviewBoxList() {
        const pcdIndexBoxListMap = get().pcdIndexReviewBoxListMap;
        const pcdIndex = getCurrentPcdIndex();

        return pcdIndexBoxListMap[pcdIndex] ?? [];
      },

      setReviewCommentVisible(visible) {
        set({
          reviewCommentVisible: visible,
        });
      },
    };
  },
);

export default useReviewStore;
